
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, Phone, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAppointments } from "@/hooks/useAppointments";
import { format } from "date-fns";

interface Provider {
  id: string;
  name: string;
  specialty: string;
  nextAvailable: string;
  rating: number;
  experience: string;
}

interface PatientInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  reason: string;
}

interface BookingFormProps {
  provider: Provider;
  selectedDate: string;
  selectedTime: string;
  patientInfo: PatientInfo;
  onPatientInfoChange: (info: Partial<PatientInfo>) => void;
  onComplete: () => void;
}

export function BookingForm({ 
  provider, 
  selectedDate, 
  selectedTime, 
  patientInfo, 
  onPatientInfoChange, 
  onComplete 
}: BookingFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { addAppointment } = useAppointments();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!patientInfo.firstName || !patientInfo.lastName || !patientInfo.email || !patientInfo.phone || !patientInfo.reason) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse the selected date to create a proper Date object
      const appointmentDate = new Date(selectedDate);
      
      // Add the appointment to the shared state
      const newAppointment = addAppointment({
        patient: `${patientInfo.firstName} ${patientInfo.lastName}`,
        provider: provider.name,
        time: selectedTime,
        duration: 30, // Default duration
        type: "Consultation", // Default type based on reason
        day: 1, // This will be recalculated in the calendar
        date: format(appointmentDate, "yyyy-MM-dd"),
        reason: patientInfo.reason,
        status: "confirmed",
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('New appointment added:', newAppointment);
      
      toast({
        title: "Booking Confirmed! 🎉",
        description: `Your appointment with ${provider.name} has been scheduled for ${selectedDate} at ${selectedTime}.`,
      });

      onComplete();
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Failed",
        description: "There was an error booking your appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Booking Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Booking Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{provider.name}</div>
                  <div className="text-sm text-blue-600">{provider.specialty}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{selectedDate}</div>
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {selectedTime}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="font-medium text-sm text-gray-700">Experience</div>
                <div className="text-sm text-gray-600">{provider.experience}</div>
              </div>
              <div>
                <div className="font-medium text-sm text-gray-700">Rating</div>
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  ⭐ {provider.rating}/5.0
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patient Information Form */}
      <Card>
        <CardHeader>
          <CardTitle>Patient Information</CardTitle>
          <CardDescription>Please provide your details to complete the booking</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={patientInfo.firstName}
                  onChange={(e) => onPatientInfoChange({ firstName: e.target.value })}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={patientInfo.lastName}
                  onChange={(e) => onPatientInfoChange({ lastName: e.target.value })}
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={patientInfo.email}
                onChange={(e) => onPatientInfoChange({ email: e.target.value })}
                placeholder="Enter your email address"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={patientInfo.phone}
                onChange={(e) => onPatientInfoChange({ phone: e.target.value })}
                placeholder="Enter your phone number"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="reason">Reason for Visit *</Label>
              <Textarea
                id="reason"
                value={patientInfo.reason}
                onChange={(e) => onPatientInfoChange({ reason: e.target.value })}
                placeholder="Briefly describe the reason for your visit"
                rows={3}
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Important Notes</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Please arrive 15 minutes before your appointment</li>
                <li>• Bring a valid ID and insurance card</li>
                <li>• You can reschedule up to 24 hours in advance</li>
              </ul>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Booking Appointment..." : "Confirm Booking"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
